import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { forEach } from 'lodash';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import RightModalLayout from '../../../layouts/Modal/RightModalLayout';
import FormProviderMC from '../../../components/FormProviderMC';
import InputMC from '../../../components/InputMC';
import DropdownMC from '../../../components/DropdownMC';
import DateRangeMC from '../../../components/DateRangeMC';
import { SIDEBAR_ACTIONS } from '../../../../constants/actionsConstants';
import { fetchActiveAgents } from '../../../../actions/agents';
import { buildOrdersFiltersSchema } from '../schemas/ordersFiltersSchema';

const EMPTY_FILTERS = {
  'q[sequential_number_end]': '',
  'q[agent_id_eq]': '',
  'q[agent_name_eq]': '',
  'q[created_at_gteq]': '',
  'q[created_at_lteq]': ''
};

const getDefaultAgentId = (defaultFilters = {}) => {
  if (defaultFilters['q[agent_name_eq]'] === 'MIA') return 'MIA';
  if (defaultFilters['q[agent_id_eq]']) return String(defaultFilters['q[agent_id_eq]']);
  return '';
};

const getDefaultDateRange = (defaultFilters = {}) => {
  const start = defaultFilters['q[created_at_gteq]'];
  const end = defaultFilters['q[created_at_lteq]'];

  if (!start || !end) return null;

  return {
    startDate: moment(start).toDate(),
    endDate: moment(end).toDate()
  };
};

const OrdersFiltersSidebar = ({ handleSearch, defaultFilters = {} }) => {
  const { t } = useTranslation();
  const { t: tOrders } = useTranslation('Orders');
  const { t: tCommon } = useTranslation('Common');
  const dispatch = useDispatch();
  const { agents } = useSelector((reduxState) => reduxState.agentsReducer);

  const [agentId, setAgentId] = useState(() => getDefaultAgentId(defaultFilters));
  const [dateRange, setDateRange] = useState(() => getDefaultDateRange(defaultFilters));

  const formMethods = useForm({
    resolver: zodResolver(buildOrdersFiltersSchema(tCommon)),
    defaultValues: {
      sequential_number: defaultFilters['q[sequential_number_end]'] || ''
    }
  });

  useEffect(() => {
    dispatch(fetchActiveAgents());
  }, [dispatch]);

  const agentOptions = useMemo(() => {
    const options = [
      { id: '', label: tOrders('filters.all_agents') },
      { id: 'MIA', label: 'MIA' }
    ];

    forEach(agents, (row) => {
      options.push({
        id: String(row.id),
        label: `${row.first_name} ${row.last_name}`.trim()
      });
    });

    return options;
  }, [agents, tOrders]);

  const buildFilters = () => {
    const sequentialNumber = formMethods.getValues('sequential_number') || '';
    let agentIdEq = '';
    let agentNameEq = '';

    if (agentId === 'MIA') {
      agentNameEq = 'MIA';
    } else if (agentId) {
      agentIdEq = agentId;
    }

    return {
      ...EMPTY_FILTERS,
      'q[sequential_number_end]': sequentialNumber,
      'q[agent_id_eq]': agentIdEq,
      'q[agent_name_eq]': agentNameEq,
      'q[created_at_gteq]': dateRange?.startDate
        ? moment(dateRange.startDate).format('YYYY-MM-DD')
        : '',
      'q[created_at_lteq]': dateRange?.endDate
        ? moment(dateRange.endDate).format('YYYY-MM-DD')
        : ''
    };
  };

  const handleApply = formMethods.handleSubmit(() => {
    handleSearch(buildFilters());
    dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
  });

  const handleClear = () => {
    formMethods.reset({ sequential_number: '' });
    setAgentId('');
    setDateRange(null);
    handleSearch(EMPTY_FILTERS);
    dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
  };

  const handleAgentSelect = (value) => {
    if (value == null || value === '') {
      setAgentId('');
      return;
    }
    setAgentId(String(value));
  };

  const handleDateChange = ({ value }) => {
    setDateRange(value);
  };

  return (
    <div className="tw-wrapper">
      <RightModalLayout
        title={t('filters_sidebar.title')}
        submitLabel={t('filters_sidebar.save_label')}
        cancelLabel={t('filters_sidebar.remove_filters_label')}
        submitAction={handleApply}
        cancelAction={handleClear}
        footerVariant="VERTICAL"
        className="tw-space-y-4"
      >
        <FormProviderMC methods={formMethods} className="tw-flex tw-flex-col tw-gap-4">
          <InputMC
            name="sequential_number"
            label={tOrders('filters.order_number')}
            placeholder={tOrders('filters.order_number_placeholder')}
            useFloatingLabel
          />

          <DropdownMC
            id="orders-agent-filter"
            name="agent_id"
            label={tOrders('filters.agent')}
            useFloatingLabel
            options={agentOptions}
            defaultValue={agentId}
            onSelect={handleAgentSelect}
            search
          />

          <DateRangeMC
            label={tOrders('filters.purchase_date')}
            name="created_at"
            defaultDate={dateRange}
            handleChange={handleDateChange}
            useFloatingLabel
          />
        </FormProviderMC>
      </RightModalLayout>
    </div>
  );
};

export default OrdersFiltersSidebar;
